import React from "react";
import { Stack, TextField, TooltipHost } from "@fluentui/react";
import { mdiCheck, mdiClose, mdiPencil } from "@mdi/js";
import { getMorPduReadings } from "../../../services/infrastructure-automation/InfrastructureAutmationService";
import { Loading } from "../../../services/Loading";
import { toFixedValue } from "../../../services/UtilService";
import { THEME } from "../../../utils/Styles";
import { IMorPduReadings } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import Icon from "@mdi/react";
import { updateCustomerRackwisePdu } from "../../../services/customers/CustomerService";
import { getFloorsWhichHasMorSensors } from "../../../services/datacenter/DatacenterService";
import moment from "moment";

export interface RackwiseBreakerLevelReadingsProps {
  searchObj: any;
  datacenterId: string;
  searchFromLogs: boolean;
}

const RackwiseBreakerLevelReadings: React.FC<RackwiseBreakerLevelReadingsProps> = ({
  searchObj,
  datacenterId,
  searchFromLogs
}) => {
  // hooks
  const { showToast } = React.useContext(ToastContext);
  // state
  const [isLoading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    isOnEdit: false,
    pduIndex: undefined,
    phaseIndex: undefined,
  } as {
    isOnEdit: boolean;
    pduIndex?: number;
    phaseIndex?: number;
    dataObj: {
      date: Date | string;
      pduId: string[];
      breakerId?: string[];
      amps?: number | string;
      kw?: number | string;
      kwh?: number | string;
      firstReading?: number | string;
      lastReading?: number | string;
    };
  });
  const [readings, setReadings] = React.useState([] as IMorPduReadings[]);

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      await searchMorReadings();
    };
    asyncFunc();
  }, [searchObj]);

  const searchMorReadings = async () => {
    setLoading(true);

    // let floorId = "";
    // const fResult = await getFloorsWhichHasMorSensors({
    //   datacenterId: datacenterId,
    //   type: "pdu",
    // });
    // // get floorId
    // if (fResult?.success) {
    //   floorId = fResult.data[0]?.floorId as string;
    // }
    const serchObj = { ...searchObj };
    serchObj.startDate = moment(searchObj.startDate).format("YYYY-MM-DD");
    if (searchFromLogs) {
      serchObj.endDate = moment(searchObj.endDate).format("YYYY-MM-DD HH:mm:ss");
    } else {
      serchObj.endDate = moment(searchObj.endDate).format("YYYY-MM-DD");
    }
    serchObj.isDedicated = false;
    serchObj.searchFromLogs = searchFromLogs;
    // if (floorId) {
    //   serchObj["floorId"] = floorId;
    // }
    // serchObj.startDate = moment(state.date).startOf("month").toDate();
    // serchObj.endDate = moment(state.date).endOf("month").toDate();
    const result = await getMorPduReadings(serchObj);
    if (result && result.success) {
      setState({
        ...state,
        isOnEdit: false,
        pduIndex: undefined,
        phaseIndex: undefined,
        dataObj: {
          ...state.dataObj,
          pduId: [],
          breakerId: [],
          amps: undefined,
          kw: undefined,
          kwh: undefined,
          firstReading: undefined,
          lastReading: undefined,
        },
      });
      setReadings(result.data);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        result.message || "Error while fetching sensors readings",
        "error"
      );
    }
  };

  const handleChange = (e: any) => {
    setState({
      ...state,
      dataObj: {
        ...state.dataObj,
        [e.target.name]: e.target.value,
      },
    });
  };

  const onUpdateRackwiseData = async () => {
    console.log("rObj", state.dataObj);
    if (!state.dataObj.amps) {
      showToast("Amps field is required", "error");
    } else if (!state.dataObj.kw) {
      showToast("KW field is required", "error");
    } else if (!state.dataObj.kwh) {
      showToast("KWH field is required", "error");
    } else {
      setLoading(true);
      let dataObj = { ...state.dataObj };
      dataObj.date = searchObj.startDate;
      dataObj.amps = Number(dataObj.amps);
      dataObj.kw = Number(dataObj.kw);
      dataObj.kwh = Number(dataObj.kwh);
      let result = await updateCustomerRackwisePdu(dataObj);
      if (result?.success) {
        let allReadings = [...readings];
        if (state.pduIndex !== undefined && state.phaseIndex !== undefined) {
          // phases = [...readings[state.pduIndex].phases]
          allReadings[state.pduIndex].phases[state.phaseIndex].amps =
            result.data?.amps || 0;
          allReadings[state.pduIndex].phases[state.phaseIndex].kw =
            result.data?.kw || 0;
          allReadings[state.pduIndex].phases[state.phaseIndex].kwh =
            result.data?.kwh || 0;
        }
        setReadings(allReadings);
        setState({
          ...state,
          isOnEdit: false,
          pduIndex: undefined,
          phaseIndex: undefined,
          dataObj: {
            ...state.dataObj,
            breakerId: [],
            pduId: [],
            kw: undefined,
            amps: undefined,
            kwh: undefined,
            firstReading: undefined,
            lastReading: undefined,
          },
        });
        setLoading(false);
        showToast("Rackwise Pdu updated successfully", "success");
      } else {
        setLoading(false);
        showToast(
          result?.message || "Error while updating rackwise pdu values",
          "error"
        );
      }
    }
  };

  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <Stack>
        <Stack.Item className="max-table-width">
          <div className="office-table bg-white">
            <table className="ms-Table bordered-table w-100">
              <thead className="html-table-thead" style={{ fontSize: 14 }}>
                <tr className="table-height-44">
                  <th>Pdu No</th>
                  <th>Breaker No</th>
                  <th>Connected Racks</th>
                  <th>Make of PDU</th>
                  <th>MeterId</th>
                  <th>kw</th>
                  <th>Amps</th>
                  <th>kwh</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {readings && readings.length > 0 ? (
                  readings.reduce((acc: any, d, index) => {
                    if (d.phases.length > 0) {
                      d.phases.forEach((p, pIndex) => {
                        acc.push(
                          <tr key={index + "" + pIndex}>
                            <td>{d.pduName}</td>
                            {/* {pIndex === 0 && (
                              <td rowSpan={d.phases.length}>{d.pduName}</td>
                            )} */}
                            <td>{p.phaseName}</td>
                            <td className={`${p.connectedTo || "bg-warning"}`}>
                              {p.connectedTo}
                            </td>
                            {/* {pIndex === 0 && (
                              <td rowSpan={d.phases.length}>{d.type}</td>
                            )} */}
                            <td>{d.type}</td>
                            <td>{p.path}</td>
                            {state.isOnEdit &&
                            state.pduIndex === index &&
                            state.phaseIndex === pIndex ? (
                              <td width={150}>
                                <TextField
                                  value={String(state.dataObj.kw)}
                                  type="number"
                                  name="kw"
                                  onChange={handleChange}
                                  // className="width-100"
                                />
                              </td>
                            ) : (
                              <td>{toFixedValue(p.kw)}</td>
                            )}
                            {state.isOnEdit &&
                            state.pduIndex === index &&
                            state.phaseIndex === pIndex ? (
                              <td width={150}>
                                <TextField
                                  value={String(state.dataObj.amps)}
                                  type="number"
                                  name="amps"
                                  onChange={handleChange}
                                  // className="width-100"
                                />
                              </td>
                            ) : (
                              <td>{toFixedValue(p.amps)}</td>
                            )}
                            {state.isOnEdit &&
                            state.pduIndex === index &&
                            state.phaseIndex === pIndex ? (
                              <td width={150}>
                                <TextField
                                  value={String(state.dataObj.kwh)}
                                  type="number"
                                  name="kwh"
                                  onChange={handleChange}
                                  // className="width-100"
                                />
                              </td>
                            ) : (
                              <td>
                                {/* {p?.kwh && p.kwh !== "NA"
                                    ? Number(p.kwh)
                                    : "NA"} */}
                                {toFixedValue(p?.kwh)}
                              </td>
                            )}
                            <td align="center">
                              {state.isOnEdit &&
                              state.pduIndex === index &&
                              state.phaseIndex === pIndex ? (
                                <React.Fragment>
                                  <TooltipHost
                                    content="Save"
                                    calloutProps={{
                                      gapSpace: 0,
                                    }}
                                  >
                                    <span
                                      onClick={() => onUpdateRackwiseData()}
                                    >
                                      <Icon
                                        path={mdiCheck}
                                        size={0.8}
                                        className="pointer"
                                        color="green"
                                      />
                                    </span>
                                  </TooltipHost>
                                  &nbsp;&nbsp;
                                  <TooltipHost
                                    content="Cancel"
                                    calloutProps={{
                                      gapSpace: 0,
                                    }}
                                  >
                                    <span
                                      onClick={() =>
                                        setState({
                                          ...state,
                                          isOnEdit: false,
                                          pduIndex: undefined,
                                          phaseIndex: undefined,
                                          dataObj: {
                                            ...state.dataObj,
                                            pduId: [],
                                            breakerId: [],
                                            amps: undefined,
                                            kw: undefined,
                                            kwh: undefined,
                                          },
                                        })
                                      }
                                    >
                                      <Icon
                                        path={mdiClose}
                                        size={0.8}
                                        className="pointer"
                                        color="red"
                                      />
                                    </span>
                                  </TooltipHost>
                                </React.Fragment>
                              ) : (
                                <React.Fragment>
                                  <TooltipHost
                                    content="Edit"
                                    calloutProps={{
                                      gapSpace: 0,
                                    }}
                                  >
                                    <span
                                      onClick={() =>
                                        setState({
                                          ...state,
                                          isOnEdit: true,
                                          pduIndex: index,
                                          phaseIndex: pIndex,
                                          dataObj: {
                                            ...state.dataObj,
                                            pduId: [d?.pduId] || [],
                                            breakerId: [p?.phaseId] || [],
                                            amps:
                                              p?.amps && p.amps !== "NA"
                                                ? p.amps
                                                : "0",
                                            kw:
                                              p?.kw && p?.kw !== "NA"
                                                ? p.kw
                                                : "0",
                                            // kwh:
                                            //   p?.kwh?.end && p.kwh.end !== "NA"
                                            //     ? Number(p.kwh.end) -
                                            //       Number(p.kwh?.start)
                                            //     : 0,
                                            kwh: p?.kwh !== "NA" ? p.kwh : "0",
                                          },
                                        })
                                      }
                                    >
                                      <Icon
                                        path={mdiPencil}
                                        size={0.7}
                                        className="pointer"
                                        color={THEME.palette.themePrimary}
                                      />
                                    </span>
                                  </TooltipHost>
                                </React.Fragment>
                              )}
                            </td>
                          </tr>
                        );
                      });
                    }
                    return acc;
                  }, [])
                ) : (
                  <tr>
                    <td colSpan={9} align="center">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Stack.Item>
      </Stack>
    </React.Fragment>
  );
};

export default RackwiseBreakerLevelReadings;
